import type { FundraisingCampaign } from "@/types/models/FundraisingCampaign";
import type {
  CreateFormExtraFieldDTO,
  FormExtraField,
  UpdateFormExtraFieldDTO,
} from "@/types/models/FormExtraField";
import { apiRequest } from "@gogive/services";
import { fundraisingCampaignRoutes } from "./fundraising-campaigns.route";

export interface FundraisingCampaignListResponse {
  rows: FundraisingCampaign[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface CreateFundraisingCampaignRequest {
  name: string;
  slug?: string;
  isDefault?: boolean;
  type?: "form" | "page" | "ticketing";
  templateSlug?: string;
  pageData?: Record<string, unknown>;
}

export interface UpdateFundraisingCampaignRequest {
  name?: string;
  slug?: string;
  status?: string;
  isDefault?: boolean;
}

export interface UpdateFundraisingCampaignBrandingsRequest {
  logoMediaId?: number | null;
  bannerMediaId?: number | null;
  logoUrl?: string | null;
  imageUrl?: string | null;
  displayName?: string | null;
  fontBody?: string | null;
  fontHeading?: string | null;
  borderRadius?: number | null;
  colorBgPrimary?: string | null;
  colorBgSecondary?: string | null;
  colorAccent?: string | null;
  colorSurface?: string | null;
  colorTextPrimary?: string | null;
  colorTextSecondary?: string | null;
  buttonUpperCase?: boolean | null;
  iconKey?: string | null;
}

export type UpdateFundraisingCampaignMessagingRequest = Partial<
  Exclude<FundraisingCampaign["messaging"], null | undefined>
>;

export type UpdateFundraisingCampaignSettingsRequest = Partial<
  Exclude<FundraisingCampaign["settings"], null | undefined>
>;

export interface UpdateFundraisingCampaignFormRequest {
  start?: Date | string | null;
  end?: Date | string | null;
  initialAmount?: number | null;
  goalAmount?: number | null;
  showDonations?: boolean;
  showProgress?: boolean;
  defaultInterval?: string;
}

export const createFundraisingCampaign = (
  input: CreateFundraisingCampaignRequest,
) =>
  apiRequest<FundraisingCampaign>(fundraisingCampaignRoutes.create, {
    method: "POST",
    data: input,
  });

export const updateFundraisingCampaign = (
  id: string,
  input: UpdateFundraisingCampaignRequest,
) =>
  apiRequest<FundraisingCampaign>(fundraisingCampaignRoutes.update(id), {
    method: "POST",
    data: input,
  });

export const deleteFundraisingCampaign = (id: string) =>
  apiRequest<FundraisingCampaign>(fundraisingCampaignRoutes.delete(id), {
    method: "DELETE",
  });

export const updateFundraisingCampaignBrandings = (
  id: string,
  input: UpdateFundraisingCampaignBrandingsRequest,
) =>
  apiRequest<FundraisingCampaign>(fundraisingCampaignRoutes.brandings(id), {
    method: "POST",
    data: input,
  });

export const updateFundraisingCampaignMessaging = (
  id: string,
  input: UpdateFundraisingCampaignMessagingRequest,
) =>
  apiRequest<FundraisingCampaign>(fundraisingCampaignRoutes.messaging(id), {
    method: "POST",
    data: input,
  });

export const updateFundraisingCampaignSettings = (
  id: string,
  input: UpdateFundraisingCampaignSettingsRequest,
) =>
  apiRequest<FundraisingCampaign>(fundraisingCampaignRoutes.settings(id), {
    method: "POST",
    data: input,
  });

export const updateFundraisingCampaignForm = (
  id: string,
  input: UpdateFundraisingCampaignFormRequest,
) =>
  apiRequest<FundraisingCampaign>(fundraisingCampaignRoutes.form(id), {
    method: "POST",
    data: input,
  });

export const updateFundraisingCampaignPage = (
  id: string,
  data: Record<string, unknown>,
) =>
  apiRequest<FundraisingCampaign>(fundraisingCampaignRoutes.page(id), {
    method: "POST",
    data: { data },
  });

export const publishFundraisingCampaignPage = (id: string) =>
  apiRequest<FundraisingCampaign>(fundraisingCampaignRoutes.publishPage(id), {
    method: "POST",
  });

export const unpublishFundraisingCampaignPage = (id: string) =>
  apiRequest<FundraisingCampaign>(fundraisingCampaignRoutes.unpublishPage(id), {
    method: "POST",
  });

export const createFundraisingCampaignFormExtraField = (
  fundraisingCampaignId: string,
  data: CreateFormExtraFieldDTO,
) =>
  apiRequest<FormExtraField>(
    fundraisingCampaignRoutes.formFields.create(fundraisingCampaignId),
    {
      method: "POST",
      data,
    },
  );

export const updateFundraisingCampaignFormExtraField = (
  fundraisingCampaignId: string,
  fieldId: number,
  data: UpdateFormExtraFieldDTO,
) =>
  apiRequest<FormExtraField>(
    fundraisingCampaignRoutes.formFields.update(fundraisingCampaignId, fieldId),
    {
      method: "PUT",
      data,
    },
  );

export const deleteFundraisingCampaignFormExtraField = (
  fundraisingCampaignId: string,
  fieldId: number,
) =>
  apiRequest<void>(
    fundraisingCampaignRoutes.formFields.delete(fundraisingCampaignId, fieldId),
    {
      method: "DELETE",
    },
  );

export const reorderFundraisingCampaignFormExtraFields = (
  fundraisingCampaignId: string,
  fieldIds: number[],
) =>
  apiRequest<FormExtraField[]>(
    fundraisingCampaignRoutes.formFields.reorder(fundraisingCampaignId),
    {
      method: "PUT",
      data: { fieldIds },
    },
  );
